import React from "react";
import { now } from "common/utils/time";
import { useApi } from "common/utils/api";
import {
  getMissionActivityEvents,
  getEventTagType,
  isRetroactiveCreate,
  isTimeShift,
  MISSION_RESOURCE_TYPES
} from "common/utils/contradictory";
import { ACTIVITIES } from "common/utils/activities";

export function useActivityHistory({
  activities,
  mission,
  cacheContradictoryInfoInStore
}) {
  const api = useApi();
  const [historyByActivityId, setHistoryByActivityId] = React.useState({});
  const [allActivityEvents, setAllActivityEvents] = React.useState([]);
  const [dismissedActivities, setDismissedActivities] = React.useState([]);
  const [expandedActivities, setExpandedActivities] = React.useState({});

  // cache invalidation on activities change
  const activitiesKeyRef = React.useRef("");
  const historyLoadedRef = React.useRef(false);

  const activitiesKey = React.useMemo(
    () => (mission?.id || "") + ":" + activities.map(a => a.id).join(","),
    [mission?.id, activities]
  );

  // invalidate cache when activities change (not on first load)
  React.useEffect(() => {
    const isFirstLoad = activitiesKeyRef.current === "";
    activitiesKeyRef.current = activitiesKey;
    if (!isFirstLoad && mission?.resourcesWithHistory) {
      delete mission.resourcesWithHistory;
      historyLoadedRef.current = false;
    }
  }, [activitiesKey]);

  // fetch persisted history
  React.useEffect(() => {
    if (!mission || !cacheContradictoryInfoInStore || historyLoadedRef.current) return;
    historyLoadedRef.current = true;

    async function loadHistory() {
      try {
        const { activityEvents, resources } = await getMissionActivityEvents(
          mission,
          api,
          cacheContradictoryInfoInStore
        );
        const dismissed = resources
          .filter(
            r =>
              r.type === MISSION_RESOURCE_TYPES.activity && r.resource.dismissedAt
          )
          .map(r => r.resource);
        setDismissedActivities(dismissed);
        const grouped = {};
        activityEvents
          .filter(e => isTimeShift(e) || e.type === "DELETE" || isRetroactiveCreate(e))
          .forEach(event => {
            const id = event.resourceId;
            if (!grouped[id]) grouped[id] = [];
            grouped[id].push(event);
          });
        setHistoryByActivityId(grouped);
        setAllActivityEvents(activityEvents);
      } catch {
        // progressive enhancement, don't block rendering
      }
    }
    loadHistory();
  }, [mission, cacheContradictoryInfoInStore]);

  // build virtual events from __virtualAction tags set by missionActions.js "create" or "edit"
  const virtualEventsByActivityId = React.useMemo(() => {
    const grouped = {};
    const timestamp = now();
    activities.forEach(a => {
      if (!a.__virtualAction) return;
      const id = a.id;
      const activityType = a.type;
      const resourceType = MISSION_RESOURCE_TYPES.activity;
      if (a.__virtualAction === "edit" && a.__virtualEdits) {
        if (!grouped[id]) grouped[id] = [];
        a.__virtualEdits.forEach(edit => {
          grouped[id].push({
            type: "UPDATE",
            resourceType,
            time: timestamp,
            submitter: null,
            __virtual: true,
            after: { ...edit.after, type: activityType },
            before: { ...edit.before, type: activityType },
            context: edit.context
          });
        });
      } else if (a.__virtualAction === "cancel") {
        if (!grouped[id]) grouped[id] = [];
        grouped[id].push({
          type: "DELETE",
          resourceType,
          time: timestamp,
          submitter: null,
          __virtual: true,
          after: null,
          before: { startTime: a.startTime, endTime: a.endTime, type: activityType },
          context: a.__virtualContext
        });
      } else if (a.__virtualAction === "create") {
        if (!grouped[id]) grouped[id] = [];
        grouped[id].push({
          type: "CREATE",
          resourceType,
          time: timestamp,
          submitter: null,
          __virtual: true,
          after: { startTime: a.startTime, endTime: a.endTime, type: activityType },
          before: null,
          context: a.__virtualContext
        });
      }
    });
    return grouped;
  }, [activities]);

  const activitiesWithIds = activities.map(a => ({
    ...a,
    id: a.id || `${a.type}${a.startTime}`
  }));

  // merge activities with their persisted + virtual events into a flat list
  const entriesWithHistory = React.useMemo(() => {
    const hasHistory = Object.keys(historyByActivityId).length > 0;
    const hasVirtual = Object.keys(virtualEventsByActivityId).length > 0;
    if (!hasHistory && !hasVirtual) return activitiesWithIds;

    const result = [];
    activitiesWithIds.forEach(activity => {
      const persistedEvents = historyByActivityId[activity.id] || [];
      const virtualEvents = virtualEventsByActivityId[activity.id] || [];
      const events = [...persistedEvents, ...virtualEvents];
      if (events.length > 0) {
        const tagType = getEventTagType(events);
        result.push({
          ...activity,
          __hasModification: true,
          __tagType: tagType
        });
      } else {
        result.push(activity);
      }
    });

    // add dismissed activities (not in main query)
    const existingIds = new Set(activitiesWithIds.map(a => a.id));
    const userIds = new Set(
      activitiesWithIds.map(a => a.userId).filter(Boolean)
    );
    const dismissedEntries = [];
    dismissedActivities.forEach(dismissed => {
      if (existingIds.has(dismissed.id)) return;
      if (!userIds.has(dismissed.userId)) return;
      const events = historyByActivityId[dismissed.id];
      if (!events || events.length === 0) return;
      const lastVersion =
        dismissed.versions && dismissed.versions.length > 0
          ? dismissed.versions[dismissed.versions.length - 1]
          : {};
      dismissedEntries.push({
        ...dismissed,
        ...lastVersion,
        id: dismissed.id,
        displayedStartTime: lastVersion.startTime || dismissed.startTime,
        displayedEndTime:
          lastVersion.endTime || dismissed.endTime || dismissed.dismissedAt,
        __hasModification: true,
        __tagType: "SUPPRESSION",
        __historyEvents: events
      });
    });

    // sort all activities chronologically and rebuild with history sub-rows
    const allActivities = [];
    result.forEach(entry => {
      if (!entry.__historyEntry) allActivities.push(entry);
    });
    dismissedEntries.forEach(entry => allActivities.push(entry));
    allActivities.sort((a, b) => {
      const timeDiff =
        (a.displayedStartTime || a.startTime) -
        (b.displayedStartTime || b.startTime);
      if (timeDiff !== 0) return timeDiff;
      const aDismissed = a.__tagType === "SUPPRESSION" ? 0 : 1;
      const bDismissed = b.__tagType === "SUPPRESSION" ? 0 : 1;
      return aDismissed - bDismissed;
    });

    const sorted = [];
    allActivities.forEach(activity => {
      sorted.push(activity);
      if (activity.__tagType === "SUPPRESSION" && activity.__historyEvents) {
        activity.__historyEvents.forEach((event, idx) => {
          sorted.push({
            id: `${activity.id}_history_${idx}`,
            __historyEntry: true,
            __event: event,
            __parentId: activity.id
          });
        });
      } else if (activity.__hasModification) {
        const persisted = historyByActivityId[activity.id] || [];
        const virtual = virtualEventsByActivityId[activity.id] || [];
        [...persisted, ...virtual].forEach((event, idx) => {
          sorted.push({
            id: `${activity.id}_history_${idx}`,
            __historyEntry: true,
            __event: event,
            __parentId: activity.id
          });
        });
      }
    });
    // tag breaks whose adjacent activities have been modified on the boundary facing the break
    const nonHistoryEntries = sorted.filter(e => !e.__historyEntry);
    nonHistoryEntries.forEach((entry, idx) => {
      if (entry.type !== ACTIVITIES.break.name) return;
      const prev = nonHistoryEntries[idx - 1];
      const next = nonHistoryEntries[idx + 1];
      if (!prev || !next) return;

      // check if the boundary facing the break was modified
      const prevPersistedEvents = historyByActivityId[prev.id] || [];
      const nextPersistedEvents = historyByActivityId[next.id] || [];

      const prevEndChanged = prev.__tagType === "AJOUT"
        || prev.__virtualAction === "create"
        || (prev.__virtualEdits || []).some(e => e.before?.endTime !== e.after?.endTime)
        || prevPersistedEvents.some(e => e.type === "UPDATE" && e.before?.endTime !== e.after?.endTime);
      const nextStartChanged = next.__tagType === "AJOUT"
        || next.__virtualAction === "create"
        || (next.__virtualEdits || []).some(e => e.before?.startTime !== e.after?.startTime)
        || nextPersistedEvents.some(e => e.type === "UPDATE" && e.before?.startTime !== e.after?.startTime);

      // fallback: if the activity is modified but has no events (split case), treat as changed
      const prevSplitFallback = !prevEndChanged && prev.__hasModification && prev.__virtualAction === "edit" && prevPersistedEvents.length === 0;
      const nextSplitFallback = !nextStartChanged && next.__hasModification && next.__virtualAction === "edit" && nextPersistedEvents.length === 0;

      if (!prevEndChanged && !nextStartChanged && !prevSplitFallback && !nextSplitFallback) return;

      // determine AJOUT vs MODIFICATION: did the gap exist before?
      const prevOriginalEnd = (prev.__virtualEdits || []).find(e => e.before?.endTime !== e.after?.endTime)?.before?.endTime
        ?? prevPersistedEvents.find(e => e.type === "UPDATE" && e.before?.endTime !== e.after?.endTime)?.before?.endTime
        ?? prev.endTime;
      const nextOriginalStart = (next.__virtualEdits || []).find(e => e.before?.startTime !== e.after?.startTime)?.before?.startTime
        ?? nextPersistedEvents.find(e => e.type === "UPDATE" && e.before?.startTime !== e.after?.startTime)?.before?.startTime
        ?? next.startTime;

      // if adjacent activity was created by manager or we hit the split fallback → gap is new (AJOUT)
      const isNewGap = prevSplitFallback || nextSplitFallback
        || prev.__virtualAction === "create"
        || next.__virtualAction === "create"
        || prev.__tagType === "AJOUT"
        || next.__tagType === "AJOUT";
      const hadGap = isNewGap ? false : prevOriginalEnd < nextOriginalStart;

      entry.__hasModification = true;
      entry.__tagType = hadGap ? "MODIFICATION" : "AJOUT";
    });

    return sorted;
  }, [
    activitiesWithIds,
    historyByActivityId,
    virtualEventsByActivityId,
    dismissedActivities
  ]);

  return {
    entriesWithHistory,
    allActivityEvents,
    expandedActivities,
    setExpandedActivities
  };
}
